import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import {
    Alert,
    Button,
    Card,
    CardBody,
    CardImg,
    Col,
    Row,
} from "reactstrap";
import LoadingScreen from "react-loading-screen";
import logo from "assets/img/logo/logo_aig_login-2.png";
import Page from "components/Page";
import { apiGet, apiPost } from "../../../../api";
import {
    urlObtenerFormasPagoRenovaciones,
    urlGuardarFormaPagoRenovacion,
    urlConsultaFVinculacionAigPymes,
} from "../../api/urls";
import { empresa, plataformaWeb } from "../../../../constants";
import { consultarUsuarioFV, setConsultarUsuarioFv } from "../../../../redux/actions/aFormularioVinculacion";
import { FetchRenovacionItem } from "../../redux/aRenovaciones";
import { stringABase64 } from "../../../../utils/Encriptar";

/**
 * FormaPagoPymes — Componente funcional para seleccionar forma de pago
 * en renovaciones PYMES (sin carrito).
 *
 * Recibe el renovacionId por query string y llama directamente al servicio
 * de métodos de pago de renovaciones.
 */
const FormaPagoPymes = (props) => {
    const { history, location, dataUsuario, formularioVinculacion } = props;

    // Estado principal
    const [loading, setLoading] = useState(true);
    const [loadingText, setLoadingText] = useState("Cargando métodos de pago...");
    const [error, setError] = useState(null);
    const [formasPago, setFormasPago] = useState([]);
    const [renovacionId, setRenovacionId] = useState(null);
    const [datosRenovacion, setDatosRenovacion] = useState(null);
    const [primaTotal, setPrimaTotal] = useState("0.00");

    /**
     * Carga inicial: obtiene renovacionId de la URL,
     * consulta la renovación y luego los métodos de pago.
     */
    const cargarDatos = useCallback(async () => {
        try {
            const queryParams = new URLSearchParams(location.search);
            const idRenovacion = queryParams.get("renovacionId");

            if (!idRenovacion) {
                setError("No se encontró un identificador de renovación válido.");
                setLoading(false);
                return;
            }

            setRenovacionId(idRenovacion);

            // 1. Obtener datos de la renovación para mostrar el valor total
            setLoadingText("Obteniendo datos de la renovación...");
            const dataRenovacion = await FetchRenovacionItem(idRenovacion);

            if (dataRenovacion && dataRenovacion.dataRenovacion) {
                const ren = dataRenovacion.dataRenovacion;
                setDatosRenovacion(ren);

                // Extraer prima total del producto
                if (ren.reDatosProducto && ren.reDatosProducto.rePrimaTotal) {
                    const total = parseFloat(ren.reDatosProducto.rePrimaTotal);
                    setPrimaTotal(
                        isNaN(total) ? "0.00" : total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    );
                }
            }

            // 2. Obtener métodos de pago desde el servicio de renovaciones
            setLoadingText("Cargando métodos de pago...");
            const bodyFormasPago = {
                empresa: empresa,
                idUsuario: dataUsuario.cliCodigoCore || "",
                ambiente: "produccion",
                idRenovacion: idRenovacion,
            };

            const response = await fetch(urlObtenerFormasPagoRenovaciones, {
                method: "post",
                headers: { Accept: "application/json" },
                body: JSON.stringify(bodyFormasPago),
            });

            const data = await response.json();

            if (data && data.transaccion && data.metodosPago) {
                setFormasPago(data.metodosPago);
            } else {
                setError("No se pudieron obtener los métodos de pago. Intente nuevamente.");
            }

            // Limpiar búsqueda FV previa
            if (props.limpiarBusquedaUsuarioFV) {
                props.limpiarBusquedaUsuarioFV();
            }
        } catch (err) {
            console.error("Error en cargarDatos FormaPagoPymes:", err);
            setError("Ocurrió un error al cargar la información. Por favor, intente nuevamente.");
        } finally {
            setLoading(false);
        }
    }, [location.search, dataUsuario]);

    useEffect(() => {
        cargarDatos();
        // eslint-disable-next-line
    }, []);

    /**
     * Seleccionar una forma de pago y guardarla en el backend.
     */
    const seleccionarFormaPago = async (formCodigo) => {
        // Si el método de pago es débito automático, redirigir directamente
        // al componente DebitoAutomaticoRenovacion sin guardar aún la forma de pago.
        if (formCodigo === "debito-automatico") {
            history.push({
                pathname: "/debito-automatico-pymes",
                search: `?renovacionId=${renovacionId}`,
            });
            return;
        }

        setLoading(true);
        setLoadingText("Guardando forma de pago...");
        setError(null);

        try {
            const body = {
                empresa: empresa,
                plataforma: plataformaWeb,
                idRenovacion: renovacionId,
                formCodigo: formCodigo,
                idConducto: "",
                idPlanPago: "",
            };

            const response = await fetch(urlGuardarFormaPagoRenovacion, {
                method: "post",
                headers: { Accept: "application/json" },
                body: JSON.stringify(body),
            });

            const data = await response.json();

            if (data && data.transaccion) {
                // Consultar formulario de vinculación adaptado al nuevo servicio
                setLoadingText("Procesando...");

                const responseFv = await fetch(urlConsultaFVinculacionAigPymes + renovacionId, {
                    method: "post",
                    headers: { Accept: "application/json" },
                });

                const fv = await responseFv.json();

                if (fv && fv.transaccion) {
                    if (fv.pasarPantallaFormulario) {
                        history.push({
                            pathname: "/formulario-vinculacion-renovacion",
                            search: `?url=${stringABase64(fv.urlFormulario)}&id=${renovacionId}`,
                        });
                    } else {
                        history.push({ pathname: "/final-cotizacionbroker-renovacion" });
                    }
                } else {
                    // Si no requiere FV, ir directo al final
                    history.push({ pathname: "/final-cotizacionbroker-renovacion" });
                }
            } else {
                const errorMsg = (data && data.mensaje) ? data.mensaje : "No se pudo guardar la forma de pago. Intente nuevamente.";
                setError(errorMsg);
            }
        } catch (err) {
            console.error("Error en seleccionarFormaPago:", err);
            setError("Ocurrió un problema técnico. Por favor, contacte a soporte.");
        } finally {
            setLoading(false);
        }
    };

    /**
     * Formato de número con 2 decimales.
     */
    const formatNumber = (value) => {
        if (!value) return "0.00";
        const num = parseFloat(value);
        if (isNaN(num)) return "0.00";
        return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    return (
        <LoadingScreen
            loading={loading || (formularioVinculacion && formularioVinculacion.usuarioFvLoading)}
            bgColor="#f1f1f1"
            spinnerColor="#001871"
            textColor="#676767"
            logoSrc={logo}
            text={loadingText}
        >
            <Page
                title="Forma de Pago"
                breadcrumbs={[{ name: "forma de pago", active: true }]}
            >
                {/* Stepper */}
                <div className="container-step">
                    <div className="wrapper-step">
                        <div className="arrow-steps clearfix-step">
                            <div className="step">
                                <span>1. Datos de Facturación</span>
                            </div>
                            <div className="step current">
                                <span>2. Forma de Pago</span>
                            </div>
                            <div className="step">
                                <span>3. Formulario de Vinculación</span>
                            </div>
                            <div className="step">
                                <span>4. Emisión</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Error global */}
                {error && (
                    <Alert color="danger" isOpen={true} toggle={() => setError(null)}>
                        <center><p>{error}</p></center>
                    </Alert>
                )}

                {/* Información del pago */}
                {!loading && formasPago.length > 0 && (
                    <Card className="flex-row">
                        <CardBody>
                            <Row>
                                <Col lg={8} md={8} sm={8} xs={12}>
                                    <h2 className="btn-color-main">PAGO - Renovación PYMES</h2>
                                    <h5 style={{ textAlign: "justify" }}>
                                        A continuación seleccione una forma de pago para continuar.
                                    </h5>
                                    <h5 style={{ textAlign: "justify" }}>
                                        El valor final que será pagado es:{" "}
                                        <strong>${primaTotal}</strong>
                                    </h5>
                                    {datosRenovacion && datosRenovacion.reNumPoliza && (
                                        <h5 style={{ textAlign: "justify" }}>
                                            Póliza: <strong>{datosRenovacion.reNumPoliza}</strong>
                                        </h5>
                                    )}
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                )}

                {/* Sin métodos de pago */}
                {!loading && formasPago.length === 0 && !error && (
                    <Alert color="warning">
                        <center>
                            <p>No se encontraron métodos de pago disponibles para esta renovación.</p>
                        </center>
                    </Alert>
                )}

                {/* Tarjetas de formas de pago */}
                {!loading &&
                    formasPago.length > 0 &&
                    formasPago.map((forma, index) => (
                        <Card className="flex-row" key={forma.formCodigo || index}>
                            <CardBody className="d-flex justify-content-center flex-column">
                                <Row>
                                    {/* Imagen de la forma de pago */}
                                    {forma.formImagen && forma.formImagen.length > 0 && (
                                        <Col lg={8} md={12} sm={12} xs={12} style={{ marginBottom: "0em" }}>
                                            <CardImg src={forma.formImagen[0]} />
                                        </Col>
                                    )}

                                    {/* Botón y descripción */}
                                    <Col lg={4} md={12} sm={12} xs={12} style={{ marginBottom: "0em" }}>
                                        <div style={{ textAlign: "center" }}>
                                            <h2 className="btn-color-main">{forma.formNombre}</h2>
                                            <h5 style={{ textAlign: "justify" }}>
                                                {forma.fomrDescripcion || forma.formDescripcion || ""}.
                                            </h5>
                                            <i className="fa fa-arrow-down" aria-hidden="true" />
                                            <hr />
                                            <Button
                                                className="btn btn-primary"
                                                onClick={() => seleccionarFormaPago(forma.formCodigo)}
                                            >
                                                <i className="fa fa-credit-card" aria-hidden="true" />{" "}
                                                {forma.formNombre}
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    ))}
            </Page>
        </LoadingScreen>
    );
};

const mapStateToProps = (state) => ({
    dataUsuario: (state.datosUsuarios && state.datosUsuarios.dataUsuario) || {},
    formularioVinculacion: state.rFormularioVinculacion || {},
});

const mapDispatchToProps = (dispatch) => ({
    consultarFv: (data) => dispatch(consultarUsuarioFV(data)),
    limpiarBusquedaUsuarioFV: () => dispatch(setConsultarUsuarioFv(null)),
});

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(FormaPagoPymes)
);
